import { Injectable, inject } from '@angular/core';

import {
	isViewAccessActive,
	type ViewAccessPublic,
	type ViewShareCodeStatus,
} from '@framework/analytics/models/view-access.model';
import { reportAnalyticsError } from '../utils/analytics-error-reporting.util';
import { RequestService } from './request.service';

const QUERY_GET_VIEW_ACCESS = `
	query getViewAccess($reportId: ID!) {
		getViewAccess(reportId: $reportId) {
			reportId
			viewId
			accountId
			accessMode
			status
			expiresAt
			client {
				name
				logo
			}
		}
	}
`;

const MUTATION_CREATE_VIEW_ACCESS_REQUEST = `
	mutation createViewAccessRequest($input: CreateViewAccessRequestInput!) {
		createViewAccessRequest(input: $input) {
			ok
		}
	}
`;

const MUTATION_SEND_VIEW_SHARE_CODE = `
	mutation sendViewShareCode($input: SendViewShareCodeInput!) {
		sendViewShareCode(input: $input) {
			status
		}
	}
`;

const MUTATION_VERIFY_VIEW_SHARE_CODE = `
	mutation verifyViewShareCode($input: VerifyViewShareCodeInput!) {
		verifyViewShareCode(input: $input) {
			token
		}
	}
`;

export type ViewAccessLookup =
	| { status: 'ok'; access: ViewAccessPublic }
	| { status: 'unavailable' };

export type SendViewShareCodeOutcome =
	| { status: 'CODE_SENT' }
	| { status: 'ACCESS_REQUIRED' }
	| { status: 'failed' };

export type VerifyViewShareCodeOutcome =
	| { status: 'ok'; token: string }
	| { status: 'invalid' }
	| { status: 'access_required' }
	| { status: 'unavailable' }
	| { status: 'failed' };

const SESSION_ACCESS_PREFIX = 'jepto_view_access:';

/**
 * Guest-IAM share gate metadata and access requests.
 *
 * Always runs on the anonymous IAM path (`executeAsGuest`) — even after a viewer has a
 * wrapper JWT — so the gate stays usable when a session expires or is revoked.
 *
 * Successful lookups are cached in memory and `sessionStorage` so parallel callers share
 * one network request. Dev API may return `null` on repeat reads but still include `client`
 * only on the first response — session keeps branding when a later read is empty.
 */
@Injectable({ providedIn: 'root' })
export class ViewAccessService {
	private readonly requestService = inject(RequestService);

	private readonly cache = new Map<string, ViewAccessLookup>();
	private readonly inflight = new Map<string, Promise<ViewAccessLookup>>();

	async getViewAccess(reportId: string): Promise<ViewAccessLookup> {
		const key = reportId.trim().toLowerCase();
		if (!key) {
			return { status: 'unavailable' };
		}

		const cached = this.cache.get(key);
		if (cached) {
			return cached;
		}

		const sessionCached = this.#readSessionAccess(key);
		if (sessionCached) {
			const lookup: ViewAccessLookup = { status: 'ok', access: sessionCached };
			this.cache.set(key, lookup);
			return lookup;
		}

		let pending = this.inflight.get(key);
		if (!pending) {
			pending = this.#fetchViewAccess(key).finally(() => {
				this.inflight.delete(key);
			});
			this.inflight.set(key, pending);
		}

		const lookup = await pending;
		if (lookup.status === 'ok') {
			const access = this.#mergeBrandingFromSession(key, lookup.access);
			const resolved: ViewAccessLookup = { status: 'ok', access };
			this.cache.set(key, resolved);
			this.#writeSessionAccess(key, access);
			return resolved;
		}
		return lookup;
	}

	#mergeBrandingFromSession(
		reportId: string,
		access: ViewAccessPublic
	): ViewAccessPublic {
		const hasClientBranding =
			!!access.client?.name?.trim() || !!access.client?.logo?.trim();
		if (hasClientBranding) {
			return access;
		}

		const session = this.#parseSessionAccess(reportId);
		if (
			!session?.client?.name?.trim() &&
			!session?.client?.logo?.trim()
		) {
			return access;
		}

		return {
			...access,
			client: {
				name: access.client?.name ?? session.client?.name ?? null,
				logo: access.client?.logo ?? session.client?.logo ?? null,
			},
		};
	}

	#readSessionAccess(reportId: string): ViewAccessPublic | null {
		const access = this.#parseSessionAccess(reportId);
		if (!access) {
			return null;
		}
		if (!this.#isSessionAccessUsable(reportId, access)) {
			this.#clearSessionAccess(reportId);
			return null;
		}
		return access;
	}

	#parseSessionAccess(reportId: string): ViewAccessPublic | null {
		if (typeof sessionStorage === 'undefined') {
			return null;
		}
		try {
			const raw = sessionStorage.getItem(
				`${SESSION_ACCESS_PREFIX}${reportId}`
			);
			return raw ? (JSON.parse(raw) as ViewAccessPublic) : null;
		} catch {
			return null;
		}
	}

	#isSessionAccessUsable(
		reportId: string,
		access: ViewAccessPublic
	): boolean {
		if (access.reportId?.trim().toLowerCase() !== reportId) {
			return false;
		}
		return isViewAccessActive(access);
	}

	#writeSessionAccess(reportId: string, access: ViewAccessPublic): void {
		if (typeof sessionStorage === 'undefined') {
			return;
		}
		try {
			sessionStorage.setItem(
				`${SESSION_ACCESS_PREFIX}${reportId}`,
				JSON.stringify(access)
			);
		} catch {
			// ignore quota / private mode
		}
	}

	#clearSessionAccess(reportId: string): void {
		if (typeof sessionStorage === 'undefined') {
			return;
		}
		try {
			sessionStorage.removeItem(`${SESSION_ACCESS_PREFIX}${reportId}`);
		} catch {
			// ignore private mode
		}
	}

	async createViewAccessRequest(
		reportId: string,
		email: string,
		note?: string
	): Promise<boolean> {
		const input: {
			reportId: string;
			email: string;
			note?: string;
		} = {
			reportId,
			email: email.trim().toLowerCase(),
		};
		const trimmedNote = note?.trim();
		if (trimmedNote) {
			input.note = trimmedNote;
		}
		try {
			const response = await this.requestService.executeAsGuest(
				MUTATION_CREATE_VIEW_ACCESS_REQUEST,
				{ input }
			);
			return response?.data?.createViewAccessRequest?.ok === true;
		} catch (error) {
			reportAnalyticsError('Failed to request report access', error, {
				feature: 'analytics',
				operation: 'createViewAccessRequest',
			});
			return false;
		}
	}

	/** Guest OTP email for PRIVATE shares (`@aws_iam`). */
	async sendViewShareCode(
		reportId: string,
		email: string
	): Promise<SendViewShareCodeOutcome> {
		const input = {
			reportId,
			email: email.trim().toLowerCase(),
		};
		try {
			const response = await this.requestService.executeAsGuest(
				MUTATION_SEND_VIEW_SHARE_CODE,
				{ input }
			);
			const result = response?.data?.sendViewShareCode as
				| { status?: ViewShareCodeStatus }
				| undefined;
			const status = result?.status;
			if (status === 'CODE_SENT') {
				return { status: 'CODE_SENT' };
			}
			if (status === 'ACCESS_REQUIRED') {
				return { status: 'ACCESS_REQUIRED' };
			}
			return { status: 'failed' };
		} catch (error) {
			reportAnalyticsError('Failed to send view share code', error, {
				feature: 'analytics',
				operation: 'sendViewShareCode',
			});
			return { status: 'failed' };
		}
	}

	/** Guest OTP verify for PRIVATE shares (`@aws_iam`). */
	async verifyViewShareCode(
		reportId: string,
		email: string,
		code: string
	): Promise<VerifyViewShareCodeOutcome> {
		try {
			const response = await this.requestService.executeAsGuest(
				MUTATION_VERIFY_VIEW_SHARE_CODE,
				{
					input: {
						reportId,
						email: email.trim().toLowerCase(),
						code: code.trim(),
					},
				}
			);
			if (this.#isGraphQlFieldUndefined(response, 'verifyViewShareCode')) {
				return { status: 'unavailable' };
			}
			const token = response?.data?.verifyViewShareCode?.token as
				| string
				| undefined;
			if (token) {
				return { status: 'ok', token };
			}
			if (this.#isInvalidShareCodeResponse(response)) {
				return { status: 'invalid' };
			}
			if (this.#isAccessRequiredResponse(response)) {
				return { status: 'access_required' };
			}
			return { status: 'failed' };
		} catch (error) {
			reportAnalyticsError('Failed to verify view share code', error, {
				feature: 'analytics',
				operation: 'verifyViewShareCode',
			});
			return { status: 'failed' };
		}
	}

	#isGraphQlFieldUndefined(response: unknown, field: string): boolean {
		const errors = (response as { errors?: Array<{ message?: string }> })
			?.errors;
		if (!Array.isArray(errors)) {
			return false;
		}
		return errors.some(error =>
			String(error?.message ?? '').includes(`'${field}'`)
		);
	}

	#isInvalidShareCodeResponse(response: unknown): boolean {
		const errors = (
			response as {
				errors?: Array<{ message?: string; errorType?: string }>;
			}
		)?.errors;
		if (!Array.isArray(errors)) {
			return false;
		}
		return errors.some(error => {
			const message = String(error?.message ?? '').toLowerCase();
			const errorType = String(error?.errorType ?? '').toLowerCase();
			return (
				message.includes('invalid verification code') ||
				message.includes('incorrect verification code') ||
				message.includes('invalid code') ||
				message.includes('incorrect code') ||
				(errorType.includes('unauthorized') &&
					message.includes('verification')) ||
				(errorType.includes('badrequest') &&
					message.includes('verification'))
			);
		});
	}

	#isAccessRequiredResponse(response: unknown): boolean {
		const errors = (
			response as {
				errors?: Array<{ message?: string; errorType?: string }>;
			}
		)?.errors;
		if (!Array.isArray(errors)) {
			return false;
		}
		return errors.some(error => {
			const message = String(error?.message ?? '').toLowerCase();
			const errorType = String(error?.errorType ?? '').toLowerCase();
			return (
				message.includes('access_required') ||
				message.includes('access required') ||
				message.includes('not on the access list') ||
				message.includes('not granted permission') ||
				message.includes('not allowed to view') ||
				(errorType.includes('accessdenied') &&
					message.includes('access'))
			);
		});
	}

	async #fetchViewAccess(reportId: string): Promise<ViewAccessLookup> {
		try {
			const response = await this.requestService.executeAsGuest(
				QUERY_GET_VIEW_ACCESS,
				{ reportId }
			);
			const access =
				(response?.data?.getViewAccess as ViewAccessPublic) ?? null;
			if (!access) {
				return { status: 'unavailable' };
			}
			return { status: 'ok', access };
		} catch (error) {
			reportAnalyticsError('Failed to load share access', error, {
				feature: 'analytics',
				operation: 'getViewAccess',
			});
			return { status: 'unavailable' };
		}
	}
}
